import {
  hasButtonContent,
  templateButtonPayload,
} from './whatsappTemplateButtons';
// Catálogo dos tipos de modelo do WhatsApp, espelhando a organização da Meta:
// categoria (Marketing, Utilidade, Autenticação) e tipo de mensagem.
// O backend monta os componentes finais; este módulo define o que o editor
// oferece, detecta o tipo de modelos existentes e monta o payload da API.

export const TEMPLATE_TYPES = Object.freeze({
  STANDARD: 'standard',
  CATALOG: 'catalog',
  ORDER_DETAILS: 'order_details',
  ORDER_STATUS: 'order_status',
  CALL_PERMISSION_REQUEST: 'call_permission_request',
  AUTHENTICATION: 'authentication',
});

export const TEMPLATE_CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];

// Types offered when creating a template, per category.
export const CATEGORY_TEMPLATE_TYPES = Object.freeze({
  MARKETING: [
    TEMPLATE_TYPES.STANDARD,
    TEMPLATE_TYPES.CATALOG,
    TEMPLATE_TYPES.ORDER_DETAILS,
    TEMPLATE_TYPES.CALL_PERMISSION_REQUEST,
  ],
  UTILITY: [TEMPLATE_TYPES.STANDARD, TEMPLATE_TYPES.CALL_PERMISSION_REQUEST],
  AUTHENTICATION: [TEMPLATE_TYPES.AUTHENTICATION],
});

// Existing templates keep opening in their type even when it is no longer offered for creation.
const EDITABLE_CATEGORY_TEMPLATE_TYPES = Object.freeze({
  ...CATEGORY_TEMPLATE_TYPES,
  UTILITY: [
    ...CATEGORY_TEMPLATE_TYPES.UTILITY,
    TEMPLATE_TYPES.ORDER_STATUS,
    TEMPLATE_TYPES.ORDER_DETAILS,
  ],
});

export const ORDER_DETAILS_BUTTON_MAX_LENGTH = 25;
export const CATALOG_BUTTON_TEXT = 'View catalog';

const ALL_HEADER_FORMATS = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];

// headerFormats: formatos de cabeçalho aceitos pela Meta para o tipo.
// sendable: o seletor da conversa consegue enviar o tipo hoje sem payload inválido.
export const TEMPLATE_TYPE_CONFIG = Object.freeze({
  [TEMPLATE_TYPES.STANDARD]: {
    key: 'STANDARD',
    icon: 'i-hugeicons-message-01',
    headerFormats: ALL_HEADER_FORMATS,
    allowsFooter: true,
    allowsCustomButtons: true,
    sendable: true,
  },
  [TEMPLATE_TYPES.CATALOG]: {
    key: 'CATALOG',
    icon: 'i-hugeicons-store-01',
    headerFormats: ['NONE'],
    allowsFooter: true,
    allowsCustomButtons: false,
    sendable: false,
  },
  [TEMPLATE_TYPES.ORDER_DETAILS]: {
    key: 'ORDER_DETAILS',
    icon: 'i-hugeicons-invoice-03',
    headerFormats: ['NONE', 'TEXT', 'IMAGE', 'DOCUMENT'],
    allowsFooter: true,
    allowsCustomButtons: false,
    sendable: false,
  },
  [TEMPLATE_TYPES.ORDER_STATUS]: {
    key: 'ORDER_STATUS',
    icon: 'i-hugeicons-package-delivered',
    headerFormats: ['NONE'],
    allowsFooter: true,
    allowsCustomButtons: false,
    sendable: true,
  },
  [TEMPLATE_TYPES.CALL_PERMISSION_REQUEST]: {
    key: 'CALL_PERMISSION_REQUEST',
    icon: 'i-hugeicons-call-add',
    headerFormats: ['NONE', 'TEXT'],
    allowsFooter: true,
    allowsCustomButtons: false,
    sendable: true,
  },
  [TEMPLATE_TYPES.AUTHENTICATION]: {
    key: 'AUTHENTICATION',
    icon: 'i-hugeicons-password-validation',
    headerFormats: ['NONE'],
    allowsFooter: false,
    allowsCustomButtons: false,
    sendable: false,
  },
});

export const getTemplateTypeConfig = type =>
  TEMPLATE_TYPE_CONFIG[type] || TEMPLATE_TYPE_CONFIG[TEMPLATE_TYPES.STANDARD];

export const typesForCategory = (category, { editing = false } = {}) =>
  (editing ? EDITABLE_CATEGORY_TEMPLATE_TYPES : CATEGORY_TEMPLATE_TYPES)[
    category
  ] || CATEGORY_TEMPLATE_TYPES.UTILITY;

export const isTypeAllowedInCategory = (type, category, options = {}) =>
  typesForCategory(category, options).includes(type);

export const defaultTypeForCategory = category => typesForCategory(category)[0];

export const categoriesForType = (type, options = {}) =>
  TEMPLATE_CATEGORIES.filter(category =>
    isTypeAllowedInCategory(type, category, options)
  );

export const defaultOrderDetailsButtonText = language =>
  language === 'en_US' ? 'Copy Pix code' : 'Copiar código Pix';

const normalizeType = value => (value || '').toString().toUpperCase();

const templateButtons = template =>
  (template?.components || [])
    .filter(component => normalizeType(component?.type) === 'BUTTONS')
    .flatMap(component => component.buttons || []);

// Detecta o tipo pelos campos e componentes retornados pela Meta.
export const detectWhatsappTemplateType = template => {
  if (!template || typeof template !== 'object') return TEMPLATE_TYPES.STANDARD;
  if (normalizeType(template.category) === 'AUTHENTICATION')
    return TEMPLATE_TYPES.AUTHENTICATION;

  const components = Array.isArray(template.components)
    ? template.components
    : [];
  if (
    components.some(
      component => normalizeType(component?.type) === 'CALL_PERMISSION_REQUEST'
    )
  )
    return TEMPLATE_TYPES.CALL_PERMISSION_REQUEST;

  const buttonTypes = templateButtons(template).map(button =>
    normalizeType(button?.type)
  );
  if (buttonTypes.includes('CATALOG')) return TEMPLATE_TYPES.CATALOG;
  if (
    buttonTypes.includes('ORDER_DETAILS') ||
    normalizeType(template.display_format) === 'ORDER_DETAILS'
  )
    return TEMPLATE_TYPES.ORDER_DETAILS;
  if (normalizeType(template.sub_category) === 'ORDER_STATUS')
    return TEMPLATE_TYPES.ORDER_STATUS;

  return TEMPLATE_TYPES.STANDARD;
};

export const isTemplateTypeSendable = template =>
  getTemplateTypeConfig(detectWhatsappTemplateType(template)).sendable;

export const orderDetailsButtonText = template =>
  templateButtons(template).find(
    button => normalizeType(button?.type) === 'ORDER_DETAILS'
  )?.text || '';

// Monta o payload enviado à API da Talkvia; os componentes da Meta são
// montados e validados no backend conforme template_type.
export const buildWhatsappTemplatePayload = (form, type) => {
  const ttl = form.message_send_ttl_seconds
    ? { message_send_ttl_seconds: Number(form.message_send_ttl_seconds) }
    : {};
  if (type === TEMPLATE_TYPES.AUTHENTICATION)
    return {
      name: form.name,
      language: form.language,
      category: 'AUTHENTICATION',
      template_type: TEMPLATE_TYPES.AUTHENTICATION,
      add_security_recommendation: form.add_security_recommendation,
      code_expiration_minutes: Number(form.code_expiration_minutes),
      ...ttl,
    };

  const config = getTemplateTypeConfig(type);
  const headerFormat = config.headerFormats.includes(form.header_format)
    ? form.header_format
    : 'NONE';
  const payload = {
    name: form.name,
    language: form.language,
    category: form.category,
    template_type: type,
    parameter_format: form.parameter_format,
    header_format: headerFormat,
    header_text: headerFormat === 'TEXT' ? form.header_text : '',
    header_handle: ['NONE', 'TEXT'].includes(headerFormat)
      ? ''
      : form.header_handle,
    body: form.body,
    footer: config.allowsFooter ? form.footer : '',
    buttons: [],
    ...ttl,
  };

  if (config.allowsCustomButtons)
    payload.buttons = (form.buttons || [])
      .filter(hasButtonContent)
      .map(templateButtonPayload);

  if (type === TEMPLATE_TYPES.ORDER_DETAILS)
    payload.order_details_button_text = (
      form.order_details_button_text || ''
    ).trim();

  return payload;
};
